'use client';

import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import ease from '@/constants/ease';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import { StyleOverride } from '@/types/craft-style-overrides';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useState } from 'react';
import { useIntersectionObserverRef } from 'rooks';


export type PageBumperProps = {
  title: string;
  links: ButtonWithLinkOrAction[];
  backgroundColor?: string;
  staticImage?: React.ReactNode | null;
  styleOverrides?: StyleOverride;
}

export const PageBumper: FC<PageBumperProps> = ({ title, links = [], backgroundColor, staticImage, styleOverrides }) => {
  const [hasSeen, setHasSeen] = useState(false);
  const { modalActionHandler } = useModalButtonActions();

  const bumperImage = (additionalClasses: string = '') => {

    if (staticImage) {
      return staticImage;
    }

    return (<Image
      src="https://front.com/webassets/rebrand/bumper-image-2x.webp"
      alt=""
      className={cn(additionalClasses, "w-full")}
      width={585}
      height={322}
    />);
  }

  const callback = (entries) => {
    let isIntersecting = false;

    if (entries && entries[0]) {
      isIntersecting = entries[0].isIntersecting;
    }

    if (isIntersecting) {
      setHasSeen(true);
    }
  };

  const [ref] = useIntersectionObserverRef(callback, { threshold: 0.1 });

  const ctaCol = (
    <div className="xl:col-span-7">
      <div className="bg-citron-500 rounded-[12px] md:max-h-[322px]">
        <div className="px-3 lg:px-10 pt-3 lg:py-10">
          <div className="flex flex-wrap flex-col gap-3 md:gap-2">
            <p className="font-500 text-30/35 md:text-36/42 tracking-tight">{title}</p>
            <ul className="flex md:flex-row items-start md:items-center">
              {links.map((link, index) => (
                <li key={`bumper-link-${link.title?.replace(/\s/g, '-') ?? index}`}>
                  {link.href.length ? (
                    <Link
                      className={cn('block', {
                        'button button-size-medium button-primary-violet': link.type === 'primary',
                        'button button-size-medium button-secondary-violet': link.type === 'secondary',
                      })}
                      href={link.href}
                    >
                      {link.title}
                    </Link>
                  ) : (
                    <button
                      className={cn('block', {
                        'button button-size-medium button-primary-blue-900': link.type === 'primary',
                        'button button-size-medium button-secondary-blue-900 !ml-[12px]': link.type === 'secondary',
                      })}
                      onClick={() => {
                        modalActionHandler(link.action);
                      }}
                    >
                      {link.title}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="px-[8px] pb-[8px]">
          <div className="relative mt-[40px]">
            <div className="inset-0 flex md:hidden overflow-hidden rounded-10 mt-3">{bumperImage()}</div>
            {!staticImage && (
              <div className="absolute inset-0 flex md:hidden flex-col justify-end items-center pb-[5%]">
                <AnimatePresence>
                  {hasSeen && (
                    <motion.div
                      initial={{ opacity: 0, y: '100%' }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        ease: ease.appear,
                        duration: 400 / 1000,
                        delay: 800 / 1000,
                      }}
                      className="px-[8%]"
                    >
                      <Image src="https://front.com/webassets/rebrand/bumper-notification.svg" alt="" width={430} height={63} className="w-full h-auto" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </div>
    </div>
  );

  const animationColumn = (
    <div className="relative hidden xl:block col-span-5 pointer-events-none ml-[-8px]">
      <div className="inset-0 overflow-hidden rounded-[12px]">{bumperImage('max-h-[322px]')}</div>
      {!staticImage && (
        <div className="absolute inset-0 flex flex-col justify-end items-center pb-[15%]">
          <AnimatePresence>
            {hasSeen && (
              <motion.div
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  ease: ease.appear,
                  duration: 400 / 1000,
                  delay: 800 / 1000,
                }}
                className="px-[15%]"
              >
                <Image src="https://front.com/webassets/rebrand/bumper-notification.svg" alt="" width={430} height={63} className="w-full h-auto" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>)}
    </div>
  );

  return (
    <SectionWrapper styleOverrides={styleOverrides} className="">
      <div ref={ref} className={cn(!styleOverrides ? 'py-4 md:py-12' : 'py-1', backgroundColor ? backgroundColor : "bg-white", 'px-[16px]')}>
        <div className="mx-auto max-w-grid">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop">
            {ctaCol}
            {animationColumn}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};
